import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconDownload,
    IconPrinter,
    IconUsers,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import OutputFormatSelect from '../../image/shared/OutputFormatSelect';
import { PAGE_OUTPUT_FORMATS } from '../../image/shared/imageOutputFormats';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    calculateAge,
    calculateAgeDifference,
    parseDateInput,
    toDateInputValue,
    validateAgeAtDate,
    validateBirthDate,
} from './ageCalculatorEngine';
import { parseAgeCalculatorShareParams } from './ageCalculatorShare';
import { getAlternateCalendarDates } from './calendarDates';
import { getZodiacSign } from './zodiacSign';
import {
    downloadAgeCalculatorImageAs,
    downloadAgeCalculatorPdf,
    printAgeCalculatorSheet,
} from './generateAgeCalculatorExport';
import { buildAgeCalculatorPrintHtml, getAgeCalculatorLogoUrl } from './ageCalculatorTemplate';
import BirthdayEventCard from './components/BirthdayEventCard';
import RashifolCard from './components/RashifolCard';
import birthdayEvents from './data/birthday-events.json';
import rashifolData from './data/rashifol.json';

const dateInputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 text-[15px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-orange-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';
const dateInputErrorClass = 'border-red-500 dark:border-red-500';

function todayStr() {
    return toDateInputValue(new Date());
}

function formatNumber(value, lang) {
    const str = typeof value === 'number'
        ? (Number.isInteger(value) ? String(value) : value.toFixed(2))
        : String(value);
    if (lang !== 'bn') return str;
    return toBnDigits(str);
}

function formatDisplayDate(date, lang) {
    const opts = { year: 'numeric', month: 'long', day: 'numeric' };
    const tag = lang === 'bn' ? 'bn-BD' : 'en-US';
    return new Intl.DateTimeFormat(tag, opts).format(date);
}

function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
}

function useCountUp(target, duration = 650, delay = 0) {
    const [count, setCount] = useState(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setCount(0);
        if (!target && target !== 0) return undefined;

        let startTime = null;
        const tick = (now) => {
            if (!startTime) startTime = now + delay;
            if (now < startTime) {
                rafRef.current = requestAnimationFrame(tick);
                return;
            }
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const current = Math.round(easeOutCubic(progress) * target);
            setCount(progress < 1 ? current : target);
            if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [target, duration, delay]);

    return count;
}

/**
 * A date answer. The value stays a YYYY-MM-DD string — that is what the engine,
 * the share link and the exports read — but it is picked from the app's own
 * Bengali calendar rather than the browser's English one.
 */
function DateField({ id, label, hint, value, onChange, max, error, required }) {
    const { t } = useTranslation();
    const selected = parseDateInput(value);
    const maxDate = parseDateInput(max);
    return (
        <div>
            <label htmlFor={id} className="mb-1 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                {label}
                {required ? <span className="text-red-600"> *</span> : null}
            </label>
            {hint && !error ? (
                <p className="mb-2 text-[11px] text-[#6B635C] dark:text-zinc-500">{hint}</p>
            ) : null}
            <DatePickerField
                id={id}
                value={selected}
                onChange={(date) => onChange(date ? toDateInputValue(date) : '')}
                placeholder={t('ageCalculator.datePlaceholder')}
                triggerClassName={`${dateInputClass} ${error ? dateInputErrorClass : ''}`}
                fromYear={1900}
                toYear={(maxDate || new Date()).getFullYear() + 100}
                disabled={maxDate ? { after: maxDate } : undefined}
                clearable
            />
            {error ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400" role="alert">{error}</p>
            ) : null}
        </div>
    );
}

function CompareToggle({ checked, onChange, title, hint }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className="flex w-full items-center gap-3 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2.5 text-left transition-colors hover:border-orange-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-orange-800"
        >
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400">
                <IconUsers size={14} stroke={1.9} />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[#1B1B1F] dark:text-zinc-100">{title}</span>
                <span className="mt-0.5 block text-[11px] text-[#6B6B75] dark:text-zinc-500">{hint}</span>
            </span>
            <span
                className={`relative h-[22px] w-9 flex-none rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-[#C4C4CB] dark:bg-zinc-600'}`}
            >
                <span
                    className={`absolute top-[3px] h-4 w-4 rounded-full bg-white shadow transition-[left] ${checked ? 'left-[17px]' : 'left-[3px]'}`}
                />
            </span>
        </button>
    );
}

function AgePart({ label, value, numericValue, lang, delay = 0 }) {
    const animatedCount = useCountUp(
        typeof numericValue === 'number' ? numericValue : 0,
        650,
        delay,
    );
    const displayValue = typeof numericValue === 'number'
        ? formatNumber(animatedCount, lang)
        : value;

    return (
        <div
            data-export-age-part
            className="min-w-[88px] max-w-[140px] flex-1 rounded-2xl border-2 border-orange-200 bg-white px-3 py-4 text-center dark:border-orange-900/40 dark:bg-zinc-900 sm:min-w-[120px] sm:max-w-[180px] sm:px-4 sm:py-5"
        >
            <p
                data-export-safe-text
                data-export-age-value
                className="text-[34px] font-black leading-none text-primary sm:text-[42px] lg:text-[46px]"
            >
                {displayValue}
            </p>
            <p className="mt-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#6B6B75] dark:text-zinc-500">
                {label}
            </p>
        </div>
    );
}

function MainAgeDisplay({ result, lang, t }) {
    if (!result?.ok) return null;

    return (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-6 dark:border-orange-900/40 dark:bg-orange-950/20">
            <p className="mb-5 text-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B6B75] dark:text-zinc-500">
                {t('ageCalculator.mainResult')}
            </p>
            <div
                data-export-main-result-row
                className="flex flex-wrap justify-center gap-2 sm:gap-4"
            >
                <AgePart label={t('ageCalculator.years')} value={formatNumber(result.exact.years, lang)} numericValue={result.exact.years} lang={lang} delay={0} />
                <AgePart label={t('ageCalculator.months')} value={formatNumber(result.exact.months, lang)} numericValue={result.exact.months} lang={lang} delay={80} />
                <AgePart label={t('ageCalculator.days')} value={formatNumber(result.exact.days, lang)} numericValue={result.exact.days} lang={lang} delay={160} />
            </div>
            {result.birthday.isToday ? (
                <p className="mt-4 text-center text-[13px] font-bold text-primary">
                    {t('ageCalculator.todayBirthday')}
                </p>
            ) : null}
        </div>
    );
}

function ExportPill({ children, outline }) {
    return (
        <span
            data-export-pill
            className={`inline-block whitespace-nowrap rounded-lg border-[1.5px] px-3 py-1.5 text-[12px] font-semibold leading-snug text-orange-800 dark:text-orange-300 ${
                outline
                    ? 'border-orange-200 bg-white dark:border-orange-900/40 dark:bg-zinc-900'
                    : 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-950/20'
            }`}
        >
            {children}
        </span>
    );
}

function ExportStatCard({ label, value, highlight }) {
    return (
        <div
            data-export-stat-card
            className={`min-h-16 min-w-0 rounded-[10px] border-[1.5px] bg-white px-2 py-2.5 dark:bg-zinc-900 ${
                highlight ? 'border-orange-200 dark:border-orange-900/40' : 'border-[#E8E8EE] dark:border-zinc-700'
            }`}
        >
            <p data-export-stat-label className="mb-1.5 break-words text-[10px] font-semibold leading-snug tracking-wide text-[#6B6B75] dark:text-zinc-500">
                {label}
            </p>
            <p
                data-export-stat-value
                className={`break-all text-[14px] font-extrabold tabular-nums leading-tight ${
                    highlight ? 'text-primary' : 'text-[#1B1B1F] dark:text-zinc-100'
                }`}
            >
                {value}
            </p>
        </div>
    );
}

function ExportInfoRow({ label, value, dir, last }) {
    return (
        <div
            data-export-info-row
            className={`flex items-center justify-between gap-4 py-2.5 ${last ? '' : 'border-b border-[#F0F0F6] dark:border-zinc-800'}`}
        >
            <span data-export-info-label className="flex-shrink-0 text-[12px] font-medium text-[#6B6B75] dark:text-zinc-500">
                {label}
            </span>
            <span
                data-export-info-value
                dir={dir}
                className={`text-right text-[13px] font-bold text-[#1B1B1F] dark:text-zinc-100 ${dir === 'rtl' ? 'text-left' : ''}`}
            >
                {value}
            </span>
        </div>
    );
}

function CalendarDatesGroup({ title, gregorian, bengali, arabic, hijriBn, lang, t }) {
    const rows = [
        { label: t('ageCalculator.gregorianDate'), value: gregorian },
        ...(bengali ? [{ label: t('ageCalculator.bengaliDate'), value: bengali }] : []),
        ...(arabic ? [{ label: t('ageCalculator.arabicDate'), value: arabic, dir: 'rtl' }] : []),
        ...(lang === 'bn' && hijriBn ? [{ label: t('ageCalculator.hijriDateBn'), value: hijriBn }] : []),
    ];

    return (
        <div data-export-calendar-group className="overflow-hidden rounded-xl border border-[#E8E8EE] bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-orange-200 bg-orange-50 px-3.5 py-2.5 dark:border-orange-900/40 dark:bg-orange-950/20">
                <p className="text-[12px] font-bold text-orange-800 dark:text-orange-300">{title}</p>
            </div>
            <div className="px-3.5">
                {rows.map((row, i) => (
                    <ExportInfoRow key={row.label} label={row.label} value={row.value} dir={row.dir} last={i === rows.length - 1} />
                ))}
            </div>
        </div>
    );
}

function ExportSectionTitle({ children }) {
    return (
        <div data-export-section-title className="mb-3.5 flex items-center gap-2">
            <span className="h-4 w-[3px] flex-shrink-0 rounded-sm bg-primary" />
            <p className="text-[14px] font-bold text-orange-800 dark:text-orange-300">{children}</p>
        </div>
    );
}

function CompareResultDisplay({ diffResult, dobDate, compareDate, lang, formatAgeLine, t }) {
    if (!diffResult?.ok) return null;

    return (
        <div>
            <div className="mb-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] text-[#6B6B75] dark:text-zinc-500">{t('ageCalculator.compareYourDob')}</span>
                    <span className="text-right text-[13px] font-semibold text-[#1B1B1F] dark:text-zinc-100">
                        {formatDisplayDate(dobDate, lang)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] text-[#6B6B75] dark:text-zinc-500">{t('ageCalculator.compareOtherDob')}</span>
                    <span className="text-right text-[13px] font-semibold text-[#1B1B1F] dark:text-zinc-100">
                        {formatDisplayDate(compareDate, lang)}
                    </span>
                </div>
            </div>

            {diffResult.sameDay ? (
                <p className="text-[13px] font-semibold text-teal-700 dark:text-teal-400">
                    {t('ageCalculator.sameBirthday')}
                </p>
            ) : (
                <>
                    <p className="mb-1 text-[12px] text-[#6B6B75] dark:text-zinc-500">
                        {diffResult.olderIndex === 1 ? t('ageCalculator.youAreOlder') : t('ageCalculator.theyAreOlder')}
                    </p>
                    <p data-export-safe-text className="text-[18px] font-extrabold text-orange-800 dark:text-orange-300">
                        {formatAgeLine(diffResult.diff.years, diffResult.diff.months, diffResult.diff.days)}
                    </p>
                    <p className="mt-1.5 text-[12px] text-[#6B6B75] dark:text-zinc-500">
                        ({formatNumber(diffResult.totalDays, lang)} {t('ageCalculator.totalDays').toLowerCase()})
                    </p>
                </>
            )}
        </div>
    );
}

export default function AgeCalculatorGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const location = useLocation();
    const usageRecordedRef = useRef(false);

    const [dob, setDob] = useState('');
    const [ageAt, setAgeAt] = useState(todayStr());
    const [diffEnabled, setDiffEnabled] = useState(false);
    const [compareDob, setCompareDob] = useState('');
    const [exporting, setExporting] = useState(null);
    // What the download writes: the result card as a PDF, or as a picture.
    const [outputFormat, setOutputFormat] = useState('pdf');

    useEffect(() => {
        const search = location.search;
        if (!search) return;

        const parsed = parseAgeCalculatorShareParams(search);
        if (parsed.dob) setDob(parsed.dob);
        setAgeAt(parsed.ageAt);
        setCompareDob(parsed.compareDob);
        setDiffEnabled(parsed.diffEnabled);
    }, [location.search]);

    const dobValidation = useMemo(() => validateBirthDate(dob), [dob]);
    const ageAtValidation = useMemo(() => validateAgeAtDate(ageAt), [ageAt]);

    const dobError = useMemo(() => {
        if (!dob) return null;
        if (!dobValidation.valid) return t(`ageCalculator.errors.${dobValidation.error}`);
        return null;
    }, [dob, dobValidation, t]);

    const ageAtError = useMemo(() => {
        if (!ageAt) return null;
        if (!ageAtValidation.valid) return t(`ageCalculator.errors.${ageAtValidation.error}`);
        return null;
    }, [ageAt, ageAtValidation, t]);

    const result = useMemo(() => {
        if (!dobValidation.valid || !ageAtValidation.valid) return null;
        return calculateAge(dobValidation.date, ageAtValidation.date);
    }, [dobValidation, ageAtValidation]);

    const calcError = useMemo(() => {
        if (!result || result.ok) return null;
        return t(`ageCalculator.errors.${result.error}`);
    }, [result, t]);

    const compareValidation = useMemo(() => {
        if (!compareDob) return null;
        return validateBirthDate(compareDob);
    }, [compareDob]);

    const compareError = useMemo(() => {
        if (!compareDob || !compareValidation) return null;
        if (!compareValidation.valid) return t(`ageCalculator.errors.${compareValidation.error}`);
        return null;
    }, [compareDob, compareValidation, t]);

    const diffResult = useMemo(() => {
        if (!diffEnabled || !dobValidation.valid) return null;
        if (!compareDob) return null;
        if (!compareValidation?.valid) return { ok: false, error: t('ageCalculator.errors.invalidDifference') };
        return calculateAgeDifference(dobValidation.date, compareValidation.date);
    }, [diffEnabled, dobValidation, compareDob, compareValidation, t]);

    const recordUsageOnce = useCallback(() => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('general/age-calculator', { category: 'general' });
    }, []);

    useEffect(() => {
        if (result?.ok && dobValidation.valid) {
            recordUsageOnce();
        }
    }, [result, dobValidation.valid, recordUsageOnce]);

    const formatAgeLine = useCallback(
        (years, months, days) =>
            `${formatNumber(years, lang)} ${t('ageCalculator.years')} ${formatNumber(months, lang)} ${t('ageCalculator.months')} ${formatNumber(days, lang)} ${t('ageCalculator.days')}`,
        [lang, t],
    );

    const alternateCalendars = useMemo(() => {
        if (!dobValidation.valid || !ageAtValidation.valid) return null;
        return {
            dob: getAlternateCalendarDates(dobValidation.date, lang),
            ageAt: getAlternateCalendarDates(ageAtValidation.date, lang),
            nextBirthday: result?.ok ? getAlternateCalendarDates(result.birthday.nextDate, lang) : null,
        };
    }, [dobValidation, ageAtValidation, result, lang]);

    const birthdayEvent = useMemo(() => {
        if (!dobValidation.valid || !dobValidation.date) return null;
        const d = dobValidation.date;
        const key = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return birthdayEvents[key] ?? null;
    }, [dobValidation]);

    const rashifolSign = useMemo(() => {
        if (!dobValidation.valid || !dobValidation.date) return null;
        const signId = getZodiacSign(dobValidation.date);
        const data = rashifolData[signId];
        if (!data) return null;
        return { signId, data };
    }, [dobValidation]);

    /**
     * The printable sheet (ageCalculatorTemplate) — the same document the বাতা
     * tracking tool prints: brand header, the age as the hero, info-card sections
     * and a footer. Print, PDF and picture are all made from it.
     */
    const buildSheetHtml = () => {
        if (!result?.ok || !dobValidation.valid || !ageAtValidation.valid) return '';

        const calendarItems = (calendars, gregorian) => (calendars ? [
            [t('ageCalculator.gregorianDate'), gregorian],
            [t('ageCalculator.bengaliDate'), calendars.bengali],
            [t('ageCalculator.arabicDate'), calendars.arabic, 'rtl'],
            ...(lang === 'bn' ? [[t('ageCalculator.hijriDateBn'), calendars.hijriBn]] : []),
        ] : []);

        const sections = [
            {
                title: t('ageCalculator.additionalResults'),
                columns: 3,
                items: [
                    [t('ageCalculator.totalYears'), formatNumber(result.totals.years.toFixed(2), lang)],
                    [t('ageCalculator.totalMonths'), formatNumber(result.totals.months, lang)],
                    [t('ageCalculator.totalWeeks'), formatNumber(result.totals.weeks, lang)],
                    [t('ageCalculator.totalDays'), formatNumber(result.totals.days, lang)],
                    [t('ageCalculator.totalHours'), formatNumber(result.totals.hours, lang)],
                    [t('ageCalculator.totalMinutes'), formatNumber(result.totals.minutes, lang)],
                ],
            },
            {
                title: t('ageCalculator.birthdayInfo'),
                items: birthdayRows.map((row) => [row.label, row.value, row.dir]),
            },
            ...(alternateCalendars ? [
                {
                    title: t('ageCalculator.calendarDates'),
                    subtitle: `${t('ageCalculator.bornOn')}`,
                    items: calendarItems(alternateCalendars.dob, formatDisplayDate(dobValidation.date, lang)),
                },
                {
                    title: t('ageCalculator.calendarDates'),
                    subtitle: `${t('ageCalculator.ageAtSummary')}`,
                    items: calendarItems(alternateCalendars.ageAt, formatDisplayDate(ageAtValidation.date, lang)),
                },
            ] : []),
        ];

        const compare = diffEnabled && diffResult?.ok && compareValidation?.valid ? {
            title: t('ageCalculator.compareSectionTitle'),
            caption: diffResult.sameDay
                ? ''
                : (diffResult.olderIndex === 1 ? t('ageCalculator.youAreOlder') : t('ageCalculator.theyAreOlder')),
            value: diffResult.sameDay
                ? t('ageCalculator.sameBirthday')
                : formatAgeLine(diffResult.diff.years, diffResult.diff.months, diffResult.diff.days),
            items: [
                [t('ageCalculator.compareYourDob'), formatDisplayDate(dobValidation.date, lang)],
                [t('ageCalculator.compareOtherDob'), formatDisplayDate(compareValidation.date, lang)],
                ...(diffResult.sameDay ? [] : [[t('ageCalculator.totalDays'), formatNumber(diffResult.totalDays, lang)]]),
            ],
        } : null;

        const notes = [
            birthdayEvent ? {
                title: t('ageCalculator.birthdayEventTitle'),
                heading: lang === 'bn' ? birthdayEvent.title_bn : birthdayEvent.title_en,
                chip: t(`ageCalculator.eventCategories.${birthdayEvent.category}`, birthdayEvent.category),
                meta: `${t('ageCalculator.birthdayEventYear')}: ${lang === 'bn' ? toBnDigits(birthdayEvent.year) : birthdayEvent.year}`,
                text: lang === 'bn' ? birthdayEvent.event_bn : birthdayEvent.event_en,
            } : null,
            rashifolSign ? {
                title: t('ageCalculator.rashifolTitle'),
                heading: lang === 'bn' ? rashifolSign.data.name_bn : rashifolSign.data.name_en,
                chip: `${t('ageCalculator.rashifolElement')}: ${lang === 'bn' ? rashifolSign.data.element_bn : rashifolSign.data.element_en}`,
                meta: `${lang === 'bn' ? rashifolSign.data.dates_bn : rashifolSign.data.dates_en} · ${t('ageCalculator.rashifolTraits')}: ${lang === 'bn' ? rashifolSign.data.traits_bn : rashifolSign.data.traits_en}`,
                text: lang === 'bn' ? rashifolSign.data.rashifol_bn : rashifolSign.data.rashifol_en,
            } : null,
        ].filter(Boolean);

        return buildAgeCalculatorPrintHtml({
            sheet: {
                hero: {
                    label: t('ageCalculator.mainResult'),
                    value: formatAgeLine(result.exact.years, result.exact.months, result.exact.days),
                    chips: [
                        `${t('ageCalculator.bornOn')}: ${formatDisplayDate(dobValidation.date, lang)}`,
                        `${t('ageCalculator.ageAtSummary')}: ${formatDisplayDate(ageAtValidation.date, lang)}`,
                    ],
                },
                sections,
                compare,
                notes,
            },
            labels: {
                title: t('ageCalculator.title'),
                documentTagline: t('ageCalculator.documentTagline'),
                generatedOn: t('ageCalculator.documentGenerated', { date: formatDisplayDate(new Date(), lang) }),
                documentDisclaimer: t('ageCalculator.documentDisclaimer'),
            },
            logoUrl: getAgeCalculatorLogoUrl(window.location.origin, lang),
        });
    };

    const handlePrint = () => {
        recordUsageOnce();
        const html = buildSheetHtml();
        if (!html) return;
        printAgeCalculatorSheet(html);
    };

    // One download, in the format picked beside it in the action bar — the
    // result card as an A4 PDF, or the same card as a picture.
    const handleDownload = async () => {
        const html = buildSheetHtml();
        if (!html || exporting) return;
        setExporting(outputFormat);
        const baseName = `age-calculator-${Date.now()}`;
        try {
            if (outputFormat === 'pdf') {
                await downloadAgeCalculatorPdf(html, `${baseName}.pdf`);
                notifications.show({ title: t('common.success'), message: t('ageCalculator.pdfDownloaded'), color: 'green' });
            } else {
                await downloadAgeCalculatorImageAs(html, outputFormat, baseName);
                notifications.show({ title: t('common.success'), message: t('ageCalculator.imageDownloaded'), color: 'green' });
            }
        } catch {
            notifications.show({ title: t('common.error'), message: t('ageCalculator.exportFailed'), color: 'red' });
        } finally {
            setExporting(null);
        }
    };

    const handleReset = () => {
        setDob('');
        setAgeAt(todayStr());
        setDiffEnabled(false);
        setCompareDob('');
        usageRecordedRef.current = false;
        window.history.replaceState(null, '', '/tools/general/age-calculator');
    };

    const hasResult = Boolean(result?.ok && dobValidation.valid && ageAtValidation.valid);
    const hasInput = Boolean(dob);
    const hasError = Boolean(calcError || dobError || ageAtError);

    const status = deriveToolStatus({
        hasInput,
        canRun: dobValidation.valid,
        hasResult,
        hasError,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('ageCalculator.statusReady'),
        success: t('ageCalculator.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`ageCalculator.howTo.step${i}`)),
        [t],
    );

    const birthdayRows = result?.ok ? [
        { label: t('ageCalculator.birthDay'), value: t(`ageCalculator.weekdays.${result.birthday.dayOfWeek}`) },
        { label: t('ageCalculator.daysUntilBirthday'), value: formatNumber(result.birthday.daysUntil, lang) },
        {
            label: t('ageCalculator.monthsDaysUntil'),
            value: `${formatNumber(result.birthday.monthsUntil, lang)} ${t('ageCalculator.months')} ${formatNumber(result.birthday.daysUntilPart, lang)} ${t('ageCalculator.days')}`,
        },
        { label: t('ageCalculator.nextBirthday'), value: formatDisplayDate(result.birthday.nextDate, lang) },
        ...(alternateCalendars?.nextBirthday?.bengali ? [{ label: t('ageCalculator.bengaliDate'), value: alternateCalendars.nextBirthday.bengali }] : []),
        ...(alternateCalendars?.nextBirthday?.arabic ? [{ label: t('ageCalculator.arabicDate'), value: alternateCalendars.nextBirthday.arabic, dir: 'rtl' }] : []),
    ] : [];

    return (
        <ToolPageLayout
            slug="general/age-calculator"
            subtitleKey="ageCalculator.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('ageCalculator.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasInput ? handleReset : undefined}
            resetLabel={t('ageCalculator.reset')}
            // Everything done with a result lives in the bar, like NID to PDF:
            // the format picker, print, and one download that names
            // the format it will write.
            primaryAction={{
                label: outputFormat === 'pdf'
                    ? t('ageCalculator.downloadPdf')
                    : t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${outputFormat}`) }),
                onClick: handleDownload,
                disabled: !hasResult || Boolean(exporting),
                loading: Boolean(exporting),
                icon: IconDownload,
            }}
            secondaryAction={[
                {
                    key: 'print',
                    label: t('ageCalculator.print'),
                    onClick: handlePrint,
                    disabled: !hasResult || Boolean(exporting),
                    icon: IconPrinter,
                },
            ]}
            actionExtra={(
                <OutputFormatSelect
                    formats={PAGE_OUTPUT_FORMATS}
                    value={outputFormat}
                    onChange={setOutputFormat}
                    disabled={Boolean(exporting)}
                />
            )}
            controls={(
                <div className="space-y-4">
                    <DateField
                        id="dob"
                        label={t('ageCalculator.dobLabel')}
                        hint={t('ageCalculator.dobHint')}
                        value={dob}
                        onChange={setDob}
                        max={todayStr()}
                        error={dobError}
                        required
                    />
                    <DateField
                        id="age-at"
                        label={t('ageCalculator.ageAtLabel')}
                        hint={t('ageCalculator.ageAtHint')}
                        value={ageAt}
                        onChange={setAgeAt}
                        error={ageAtError}
                    />
                    <CompareToggle
                        checked={diffEnabled}
                        onChange={setDiffEnabled}
                        title={t('ageCalculator.ageDifference')}
                        hint={t('ageCalculator.ageDifferenceHint')}
                    />
                    {diffEnabled ? (
                        <div className="space-y-2">
                            <DateField
                                id="compare-dob"
                                label={t('ageCalculator.compareDobLabel')}
                                hint={t('ageCalculator.compareDobHint')}
                                value={compareDob}
                                onChange={setCompareDob}
                                max={todayStr()}
                                error={compareError}
                            />
                            {diffResult && !diffResult.ok && compareDob ? (
                                <p className="text-[12px] text-red-600 dark:text-red-400" role="alert">
                                    {diffResult.error}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('ageCalculator.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('ageCalculator.enterDob')}
                    idleHint={t('ageCalculator.previewEmptyHint')}
                    showIdle={!hasResult}
                    tall
                    fill
                    flushContent
                >
                    {calcError ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400">
                            {calcError}
                        </div>
                    ) : null}
                    {hasResult ? (
                        <div
                            data-age-calc-export
                            className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-lg bg-[#FBFAF9] p-3 dark:bg-zinc-950 sm:p-4"
                        >
                            <div
                                data-export-gradient-bg
                                className="rounded-xl border-[1.5px] border-orange-200 bg-orange-50 p-3 dark:border-orange-900/40 dark:bg-orange-950/20 sm:p-4"
                            >
                                <div data-export-header-row className="flex flex-col gap-3">
                                    <div>
                                        <p data-export-header-title className="text-[18px] font-extrabold leading-tight text-orange-800 dark:text-orange-300 sm:text-[20px]">
                                            {t('ageCalculator.title')}
                                        </p>
                                        <p data-export-header-meta className="mt-1 text-[12px] text-[#6B6B75] dark:text-zinc-500">
                                            SohozKaj · {formatDisplayDate(new Date(), lang)}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <ExportPill>
                                            {t('ageCalculator.bornOn')}: {formatDisplayDate(dobValidation.date, lang)}
                                        </ExportPill>
                                        <ExportPill outline>
                                            {t('ageCalculator.ageAtSummary')}: {formatDisplayDate(ageAtValidation.date, lang)}
                                        </ExportPill>
                                    </div>
                                </div>
                            </div>

                            <MainAgeDisplay result={result} lang={lang} t={t} />

                            <div data-export-stats-block data-export-block>
                                <ExportSectionTitle>{t('ageCalculator.additionalResults')}</ExportSectionTitle>
                                <div data-export-stats-grid className="mb-4 grid grid-cols-2 gap-2">
                                    <ExportStatCard label={t('ageCalculator.totalMonths')} value={formatNumber(result.totals.months, lang)} highlight />
                                    <ExportStatCard label={t('ageCalculator.totalWeeks')} value={formatNumber(result.totals.weeks, lang)} />
                                    <ExportStatCard label={t('ageCalculator.totalDays')} value={formatNumber(result.totals.days, lang)} highlight />
                                    <ExportStatCard label={t('ageCalculator.totalHours')} value={formatNumber(result.totals.hours, lang)} />
                                    <ExportStatCard label={t('ageCalculator.totalMinutes')} value={formatNumber(result.totals.minutes, lang)} />
                                </div>
                            </div>

                            {alternateCalendars ? (
                                <div data-export-calendar-block data-export-block>
                                    <ExportSectionTitle>{t('ageCalculator.calendarDates')}</ExportSectionTitle>
                                    <div data-export-calendar-grid className="mb-4 grid grid-cols-1 gap-4">
                                        <CalendarDatesGroup
                                            title={t('ageCalculator.bornOn')}
                                            gregorian={formatDisplayDate(dobValidation.date, lang)}
                                            bengali={alternateCalendars.dob.bengali}
                                            arabic={alternateCalendars.dob.arabic}
                                            hijriBn={alternateCalendars.dob.hijriBn}
                                            lang={lang}
                                            t={t}
                                        />
                                        <CalendarDatesGroup
                                            title={t('ageCalculator.ageAtSummary')}
                                            gregorian={formatDisplayDate(ageAtValidation.date, lang)}
                                            bengali={alternateCalendars.ageAt.bengali}
                                            arabic={alternateCalendars.ageAt.arabic}
                                            hijriBn={alternateCalendars.ageAt.hijriBn}
                                            lang={lang}
                                            t={t}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <div data-export-divider className="h-px bg-[#E8E8EE] dark:bg-zinc-800" />

                            <div data-export-two-col data-export-block className="mb-4 grid grid-cols-1 gap-4">
                                <div className="overflow-hidden rounded-xl border border-[#E8E8EE] bg-white dark:border-zinc-700 dark:bg-zinc-900">
                                    <div className="flex items-center gap-2 border-b border-[#E8E8EE] bg-[#F7F7F9] px-3.5 py-2.5 dark:border-zinc-700 dark:bg-zinc-800">
                                        <span className="h-4 w-[3px] flex-shrink-0 rounded-sm bg-primary" />
                                        <p className="text-[13px] font-bold text-orange-800 dark:text-orange-300">
                                            {t('ageCalculator.birthdayInfo')}
                                        </p>
                                    </div>
                                    <div className="px-3.5">
                                        {birthdayRows.map((row, i) => (
                                            <ExportInfoRow key={row.label} label={row.label} value={row.value} dir={row.dir} last={i === birthdayRows.length - 1} />
                                        ))}
                                    </div>
                                </div>

                                {birthdayEvent ? (
                                    <BirthdayEventCard event={birthdayEvent} lang={lang} />
                                ) : null}
                            </div>

                            {rashifolSign ? (
                                <div data-export-block className="mb-4">
                                    <RashifolCard signId={rashifolSign.signId} data={rashifolSign.data} lang={lang} />
                                </div>
                            ) : null}

                            {diffEnabled && diffResult?.ok && compareDob && compareValidation?.valid ? (
                                <>
                                    <div data-export-divider className="h-px bg-[#E8E8EE] dark:bg-zinc-800" />
                                    <div
                                        data-export-compare-box
                                        className="rounded-xl border-[1.5px] border-orange-200 bg-white p-4 dark:border-orange-900/40 dark:bg-zinc-900"
                                    >
                                        <ExportSectionTitle>{t('ageCalculator.compareSectionTitle')}</ExportSectionTitle>
                                        <CompareResultDisplay
                                            diffResult={diffResult}
                                            dobDate={dobValidation.date}
                                            compareDate={compareValidation.date}
                                            lang={lang}
                                            formatAgeLine={formatAgeLine}
                                            t={t}
                                        />
                                    </div>
                                </>
                            ) : null}

                            <p data-export-footer className="mt-2 text-center text-[11px] text-[#9ca3af] dark:text-zinc-600">
                                sohozkaj.com
                            </p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
